import { parseSquare, squareName } from '../board.js';
import { RuleError } from '../errors.js';
import { legalIceActions, warriorAt } from './moves.js';
import type { IceAction, IceState, WarriorType } from './types.js';

export const WARRIOR_CODES: Readonly<Record<WarriorType, string>> = {
  skier: 'Sk',
  spear: 'Sp',
  huscarl: 'Hu',
};

const TYPE_OF_CODE = new Map(
  Object.entries(WARRIOR_CODES).map(([type, code]) => [code, type as WarriorType]),
);

/**
 * Notation of an action in the state before it: `+Sp d2` (place), `Sk e2-e4` (move),
 * `Hu d2-d3>e4` (walk to d3, shove e4), `Hu d3>e4` (shove without walking), `pass`.
 */
export function toIceNotation(state: IceState, action: IceAction): string {
  switch (action.kind) {
    case 'place':
      return `+${WARRIOR_CODES[action.type]} ${squareName(action.at.r, action.at.c)}`;
    case 'pass':
      return 'pass';
    case 'move':
    case 'shove': {
      const warrior = state.warriors.find((w) => w.id === action.id);
      if (!warrior) throw new RuleError('UNKNOWN_PIECE', `No warrior ${action.id} on the ice`);
      const start = `${WARRIOR_CODES[warrior.type]} ${squareName(warrior.r, warrior.c)}`;
      if (action.kind === 'move') return `${start}-${squareName(action.to.r, action.to.c)}`;
      const walks = action.from.r !== warrior.r || action.from.c !== warrior.c;
      const walk = walks ? `-${squareName(action.from.r, action.from.c)}` : '';
      return `${start}${walk}>${squareName(action.target.r, action.target.c)}`;
    }
  }
}

const SQUARE = '([a-i][1-9])';
const PLACE = new RegExp(`^\\+(Sk|Sp|Hu) ${SQUARE}$`);
const MOVE = new RegExp(`^(Sk|Sp|Hu) ${SQUARE}(?:-${SQUARE})?(?:>${SQUARE})?$`);

/**
 * Parses notation for the side to move and returns the matching legal action. Throws
 * `BAD_NOTATION` for unreadable text and `ILLEGAL_ACTION` when it is not legal here.
 */
export function fromIceNotation(state: IceState, text: string): IceAction {
  const trimmed = text.trim();
  let action: IceAction;
  const place = PLACE.exec(trimmed);
  const move = MOVE.exec(trimmed);
  if (trimmed === 'pass') {
    action = { kind: 'pass' };
  } else if (place) {
    const type = TYPE_OF_CODE.get(place[1] ?? '');
    if (!type) throw new RuleError('BAD_NOTATION', `Unknown warrior in "${text}"`);
    action = { kind: 'place', type, at: parseSquare(place[2] ?? '') };
  } else if (move && (move[3] !== undefined || move[4] !== undefined)) {
    const [, code, startName = '', walkTo, shoveAt] = move;
    const start = parseSquare(startName);
    const warrior = warriorAt(state, start.r, start.c);
    if (!warrior || warrior.side !== state.turn || WARRIOR_CODES[warrior.type] !== code) {
      throw new RuleError('ILLEGAL_ACTION', `No ${code ?? ''} of the side to move on ${startName}`);
    }
    action =
      shoveAt === undefined
        ? { kind: 'move', id: warrior.id, to: parseSquare(walkTo ?? '') }
        : {
            kind: 'shove',
            id: warrior.id,
            from: walkTo === undefined ? start : parseSquare(walkTo),
            target: parseSquare(shoveAt),
          };
  } else {
    throw new RuleError('BAD_NOTATION', `Unreadable move: "${text}"`);
  }
  const legal = legalIceActions(state).some((a) => JSON.stringify(a) === JSON.stringify(action));
  if (!legal) throw new RuleError('ILLEGAL_ACTION', `Not legal here: "${text}"`);
  return action;
}
